import React, { useState, useEffect } from "react";
import MaterialTable from "material-table";
import {
    Grid,
    Typography,
    Button,
    Modal,
    TextField,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Chip,
    Switch,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    IconButton,
    Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import AlertDialog from "../components/AlertDialog";
import { colors } from "../components/Theme/WebTheme";
import CircularLoading from "../components/CircularLoading";
import { FirebaseConfig } from "config/FirebaseConfig";

const useStyles = makeStyles((theme) => ({
    root: {
        "& > *": {
            margin: theme.spacing(1),
        },
    },
    container: {
        width: "100%",
        marginTop: theme.spacing(1),
        alignContent: "center",
        borderRadius: "8px",
    },
    title: {
        color: colors.WHITE,
        padding: "10px",
    },
    statusChip: {
        margin: theme.spacing(0.5),
    },
    modal: {
        display: "flex",
        padding: theme.spacing(1),
        alignItems: "center",
        justifyContent: "center",
    },
    paper: {
        width: 600,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    receiptImage: {
        maxWidth: "100%",
        maxHeight: 300,
        borderRadius: 8,
        marginTop: 16,
    },
    actionButtons: {
        marginTop: 16,
        display: "flex",
        gap: 8,
    },
    approveButton: {
        backgroundColor: colors.GREEN,
        color: colors.WHITE,
        "&:hover": {
            backgroundColor: colors.GREEN,
        },
    },
    rejectButton: {
        backgroundColor: colors.RED,
        color: colors.WHITE,
        "&:hover": {
            backgroundColor: colors.RED,
        },
    },
}));

export default function YappyWalletTopups(props) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const classes = useStyles();
    const auth = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const [topupRequests, setTopupRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [receiptModalOpen, setReceiptModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

    // Yappy settings state
    const [yappySettings, setYappySettings] = useState({
        paymentLink: "",
        requireReceipt: false,
    });
    const [settingsLoading, setSettingsLoading] = useState(false);

    const serverUrl = `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net`;

    useEffect(() => {
        loadTopupRequests();
        loadYappySettings();
    }, []);

    const loadTopupRequests = async () => {
        try {
            setLoading(true);

            // Get wallet requests from Firebase
            const response = await fetch(`${serverUrl}/getWalletRequests`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();
                setTopupRequests(data.requests || []);
            } else {
                console.error("Failed to load top-up requests");
            }
        } catch (error) {
            console.error("Error loading top-up requests:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadYappySettings = async () => {
        try {
            setSettingsLoading(true);
            const response = await fetch(`${serverUrl}/getYappySettings`);
            const data = await response.json();

            if (data.success) {
                setYappySettings(data.settings);
            } else {
                console.error("Failed to load Yappy settings:", data.error);
                // Set default values if failed to load
                setYappySettings({
                    paymentLink:
                        "https://link.yappy.com.pa/stc/ba%2FyH8jGq5O%2B6XYEaAXOzhSk16Vdr9BZvaim7nGhYrA%3D",
                    requireReceipt: false,
                });
            }
        } catch (error) {
            console.error("Error loading Yappy settings:", error);
            // Set default values if error
            setYappySettings({
                paymentLink:
                    "https://link.yappy.com.pa/stc/ba%2FyH8jGq5O%2B6XYEaAXOzhSk16Vdr9BZvaim7nGhYrA%3D",
                requireReceipt: false,
            });
        } finally {
            setSettingsLoading(false);
        }
    };

    const saveYappySettings = async () => {
        try {
            setSettingsLoading(true);
            const response = await fetch(`${serverUrl}/saveYappySettings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(yappySettings),
            });
            const data = await response.json();

            if (data.success) {
                setCommonAlert({
                    open: true,
                    msg: "Yappy settings saved successfully",
                });
            } else {
                console.error("Failed to save Yappy settings:", data.error);
                setCommonAlert({
                    open: true,
                    msg: "Failed to save Yappy settings",
                });
            }
        } catch (error) {
            console.error("Error saving Yappy settings:", error);
            setCommonAlert({
                open: true,
                msg: "Error saving Yappy settings",
            });
        } finally {
            setSettingsLoading(false);
        }
    };

    const handleApproveTopup = async (request) => {
        try {
            const response = await fetch(`${serverUrl}/approveWalletTopup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    orderId: request.orderId,
                    adminUserId: auth.profile.uid,
                }),
            });

            const result = await response.json();

            if (result.success) {
                setCommonAlert({
                    open: true,
                    msg: t("wallet_topup_approved_successfully"),
                });
                setDetailModalOpen(false);
                loadTopupRequests(); // Reload the list
            } else {
                setCommonAlert({
                    open: true,
                    msg: result.error || t("wallet_topup_approval_failed"),
                });
            }
        } catch (error) {
            console.error("Error approving top-up:", error);
            setCommonAlert({
                open: true,
                msg: t("wallet_topup_approval_failed"),
            });
        }
    };

    const handleRejectTopup = async () => {
        if (!selectedRequest) return;

        try {
            const response = await fetch(`${serverUrl}/rejectWalletTopup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    orderId: selectedRequest.orderId,
                    adminUserId: auth.profile.uid,
                    reason: rejectReason,
                }),
            });

            const result = await response.json();

            if (result.success) {
                setCommonAlert({
                    open: true,
                    msg: t("wallet_topup_rejected_successfully"),
                });
                setRejectModalOpen(false);
                setDetailModalOpen(false);
                setRejectReason("");
                loadTopupRequests(); // Reload the list
            } else {
                setCommonAlert({
                    open: true,
                    msg: result.error || t("wallet_topup_rejection_failed"),
                });
            }
        } catch (error) {
            console.error("Error rejecting top-up:", error);
            setCommonAlert({
                open: true,
                msg: t("wallet_topup_rejection_failed"),
            });
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "pending":
                return "warning";
            case "approved":
                return "success";
            case "rejected":
                return "error";
            default:
                return "default";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "pending":
                return t("pending");
            case "approved":
                return t("approved");
            case "rejected":
                return t("rejected");
            default:
                return status;
        }
    };

    const columns = [
        {
            title: t("created_at"),
            field: "createdAt",
            editable: "never",
            defaultSort: "desc",
            render: (rowData) =>
                rowData.createdAt
                    ? moment(rowData.createdAt).format("lll")
                    : null,
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "left" },
        },
        {
            title: t("order_id"),
            field: "orderId",
            editable: "never",
            cellStyle: { paddingLeft: isRTL === "rtl" ? 40 : null },
        },
        {
            title: t("user_name"),
            field: "userName",
            editable: "never",
            cellStyle: { paddingLeft: isRTL === "rtl" ? 40 : null },
        },
        {
            title: t("user_email"),
            field: "userEmail",
            editable: "never",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "left" },
        },
        {
            title: t("amount"),
            field: "amount",
            editable: "never",
            render: (rowData) =>
                `${rowData.currency} ${parseFloat(rowData.amount).toFixed(2)}`,
            cellStyle: { paddingLeft: isRTL === "rtl" ? 40 : null },
        },
        {
            title: t("payment_method"),
            field: "paymentMethod",
            editable: "never",
            render: (rowData) => (
                <Chip
                    label={
                        rowData.paymentMethod === "yappy"
                            ? t("yappy_payment")
                            : rowData.paymentMethod
                    }
                    color="primary"
                    size="small"
                    className={classes.statusChip}
                />
            ),
            cellStyle: { paddingLeft: isRTL === "rtl" ? 40 : null },
        },
        {
            title: t("status"),
            field: "status",
            editable: "never",
            render: (rowData) => (
                <Chip
                    label={getStatusText(rowData.status)}
                    color={getStatusColor(rowData.status)}
                    size="small"
                    className={classes.statusChip}
                />
            ),
            cellStyle: { paddingLeft: isRTL === "rtl" ? 40 : null },
        },
        {
            title: t("receipt"),
            field: "receiptUrl",
            editable: "never",
            render: (rowData) =>
                rowData.receiptUrl ? (
                    <Tooltip title={t("view_receipt")}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRequest(rowData);
                                setReceiptModalOpen(true);
                            }}
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        No receipt
                    </Typography>
                ),
            cellStyle: { paddingLeft: isRTL === "rtl" ? 40 : null },
        },
    ];

    const handleRowClick = (event, rowData) => {
        setSelectedRequest(rowData);
        setDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setDetailModalOpen(false);
        setSelectedRequest(null);
    };

    const handleCloseReceiptModal = () => {
        setReceiptModalOpen(false);
        setSelectedRequest(null);
    };

    const handleCloseRejectModal = () => {
        setRejectModalOpen(false);
        setRejectReason("");
    };

    const handleCommonAlertClose = () => {
        setCommonAlert({ open: false, msg: "" });
    };

    if (loading) {
        return <CircularLoading />;
    }

    return (
        <div
            className={classes.container}
            style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
        >
            <div
                style={{
                    backgroundColor: colors.SECONDARY,
                    borderRadius: "8px",
                    marginBottom: 20,
                }}
            >
                <Grid item xs={12}>
                    <Typography
                        component="h1"
                        variant="h5"
                        className={classes.title}
                        style={{
                            textAlign: isRTL === "rtl" ? "right" : "left",
                        }}
                    >
                        {t("yappy_wallet_topups")}
                    </Typography>
                </Grid>
            </div>

            {/* Yappy Settings Accordion */}
            <Accordion style={{ marginBottom: 20 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Yappy Payment Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Yappy Payment Link"
                                variant="outlined"
                                value={yappySettings.paymentLink}
                                onChange={(e) =>
                                    setYappySettings({
                                        ...yappySettings,
                                        paymentLink: e.target.value,
                                    })
                                }
                                helperText="Enter the Yappy payment link that users will be redirected to"
                                disabled={settingsLoading}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={yappySettings.requireReceipt}
                                        onChange={(e) =>
                                            setYappySettings({
                                                ...yappySettings,
                                                requireReceipt:
                                                    e.target.checked,
                                            })
                                        }
                                        disabled={settingsLoading}
                                    />
                                }
                                label="Require Receipt Upload"
                            />
                            <Typography
                                variant="caption"
                                display="block"
                                style={{ marginTop: 8 }}
                            >
                                If enabled, users must upload a payment receipt
                                for verification
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={saveYappySettings}
                                disabled={settingsLoading}
                                style={{ marginRight: 10 }}
                            >
                                {settingsLoading
                                    ? "Saving..."
                                    : "Save Settings"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={loadYappySettings}
                                disabled={settingsLoading}
                            >
                                Reset to Default
                            </Button>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <MaterialTable
                title={t("wallet_topup_requests")}
                columns={columns}
                data={topupRequests}
                onRowClick={handleRowClick}
                isLoading={loading}
                style={{
                    direction: isRTL === "rtl" ? "rtl" : "ltr",
                    padding: 10,
                    borderRadius: "8px",
                    boxShadow: "4px 4px 6px #9E9E9E",
                }}
                options={{
                    exportButton: true,
                    filtering: true,
                    sorting: true,
                    pageSize: 10,
                    pageSizeOptions: [10, 20, 50],
                    headerStyle: {
                        position: "sticky",
                        top: "0px",
                        backgroundColor: colors.SECONDARY,
                        color: colors.WHITE,
                        fontSize: "0.8em",
                        fontWeight: "bold",
                        fontFamily: 'Lucida Console", "Courier New", monospace',
                    },
                    rowStyle: (rowData) => ({
                        backgroundColor: colors.WHITE,
                        cursor: "pointer",
                    }),
                }}
                localization={{
                    body: {
                        emptyDataSourceMessage: t("no_topup_requests_found"),
                    },
                    toolbar: {
                        searchTooltip: t("search"),
                        searchPlaceholder: t("search_placeholder"),
                    },
                    pagination: {
                        labelRowsSelect: t("rows_per_page"),
                        labelDisplayedRows: `{from}-{to} ${t("of")} {count}`,
                        firstTooltip: t("first_page"),
                        previousTooltip: t("previous_page"),
                        nextTooltip: t("next_page"),
                        lastTooltip: t("last_page"),
                    },
                }}
            />

            {/* Detail Modal */}
            <Modal
                open={detailModalOpen}
                onClose={handleCloseDetailModal}
                className={classes.modal}
            >
                <div className={classes.paper}>
                    {selectedRequest && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {t("topup_request_details")}
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("order_id")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.orderId}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("status")}:
                                    </Typography>
                                    <Chip
                                        label={getStatusText(
                                            selectedRequest.status
                                        )}
                                        color={getStatusColor(
                                            selectedRequest.status
                                        )}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("user_name")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.userName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("user_email")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.userEmail}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("user_mobile")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.userMobile}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("amount")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.currency}{" "}
                                        {parseFloat(
                                            selectedRequest.amount
                                        ).toFixed(2)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("payment_method")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {t("yappy_payment")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("created_at")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {moment(
                                            selectedRequest.createdAt
                                        ).format("lll")}
                                    </Typography>
                                </Grid>
                            </Grid>

                            {selectedRequest.receiptUrl && (
                                <div style={{ marginTop: 16 }}>
                                    <Typography variant="subtitle2">
                                        {t("payment_receipt")}:
                                    </Typography>
                                    <img
                                        src={selectedRequest.receiptUrl}
                                        alt="Payment Receipt"
                                        className={classes.receiptImage}
                                        onError={(e) => {
                                            console.error(
                                                "Error loading receipt image:",
                                                e
                                            );
                                            e.target.style.display = "none";
                                            e.target.nextSibling.style.display =
                                                "block";
                                        }}
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: 300,
                                            borderRadius: 8,
                                            marginTop: 8,
                                            border: "1px solid #ddd",
                                            boxShadow:
                                                "0 2px 4px rgba(0,0,0,0.1)",
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        style={{
                                            display: "none",
                                            marginTop: 8,
                                        }}
                                        id="receipt-error"
                                    >
                                        Failed to load receipt image. Please
                                        check the URL.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                            window.open(
                                                selectedRequest.receiptUrl,
                                                "_blank"
                                            )
                                        }
                                        style={{ marginTop: 8 }}
                                    >
                                        Open Receipt in New Tab
                                    </Button>
                                </div>
                            )}

                            {selectedRequest.status === "pending" && (
                                <div className={classes.actionButtons}>
                                    <Button
                                        variant="contained"
                                        className={classes.approveButton}
                                        onClick={() =>
                                            handleApproveTopup(selectedRequest)
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        className={classes.rejectButton}
                                        onClick={() => setRejectModalOpen(true)}
                                    >
                                        {t("reject")}
                                    </Button>
                                </div>
                            )}

                            <Button
                                variant="outlined"
                                onClick={handleCloseDetailModal}
                                style={{ marginTop: 16 }}
                            >
                                {t("close")}
                            </Button>
                        </>
                    )}
                </div>
            </Modal>

            {/* Receipt Modal */}
            <Modal
                open={receiptModalOpen}
                onClose={handleCloseReceiptModal}
                className={classes.modal}
            >
                <div className={classes.paper}>
                    {selectedRequest && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {t("payment_receipt")} -{" "}
                                {selectedRequest.orderId}
                            </Typography>

                            <Grid
                                container
                                spacing={2}
                                style={{ marginBottom: 16 }}
                            >
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("user_name")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.userName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("amount")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.currency}{" "}
                                        {parseFloat(
                                            selectedRequest.amount
                                        ).toFixed(2)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("status")}:
                                    </Typography>
                                    <Chip
                                        label={getStatusText(
                                            selectedRequest.status
                                        )}
                                        color={getStatusColor(
                                            selectedRequest.status
                                        )}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">
                                        {t("created_at")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {moment(
                                            selectedRequest.createdAt
                                        ).format("lll")}
                                    </Typography>
                                </Grid>
                            </Grid>

                            {selectedRequest.receiptUrl ? (
                                <div style={{ textAlign: "center" }}>
                                    <img
                                        src={selectedRequest.receiptUrl}
                                        alt="Payment Receipt"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: 400,
                                            borderRadius: 8,
                                            border: "1px solid #ddd",
                                            boxShadow:
                                                "0 2px 4px rgba(0,0,0,0.1)",
                                        }}
                                        onError={(e) => {
                                            console.error(
                                                "Error loading receipt image:",
                                                e
                                            );
                                            e.target.style.display = "none";
                                            e.target.nextSibling.style.display =
                                                "block";
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        style={{
                                            display: "none",
                                            marginTop: 8,
                                        }}
                                    >
                                        Failed to load receipt image. Please
                                        check the URL.
                                    </Typography>
                                    <div style={{ marginTop: 16 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() =>
                                                window.open(
                                                    selectedRequest.receiptUrl,
                                                    "_blank"
                                                )
                                            }
                                            style={{ marginRight: 8 }}
                                        >
                                            Open in New Tab
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleCloseReceiptModal}
                                        >
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    style={{ textAlign: "center", padding: 20 }}
                                >
                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                    >
                                        No receipt uploaded for this transaction
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCloseReceiptModal}
                                        style={{ marginTop: 16 }}
                                    >
                                        Close
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Modal>

            {/* Reject Modal */}
            <Modal
                open={rejectModalOpen}
                onClose={handleCloseRejectModal}
                className={classes.modal}
            >
                <div className={classes.paper}>
                    <Typography variant="h6" gutterBottom>
                        {t("reject_topup_request")}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        label={t("rejection_reason")}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        style={{ marginBottom: 16 }}
                    />

                    <div className={classes.actionButtons}>
                        <Button
                            variant="contained"
                            className={classes.rejectButton}
                            onClick={handleRejectTopup}
                            disabled={!rejectReason.trim()}
                        >
                            {t("reject")}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleCloseRejectModal}
                        >
                            {t("cancel")}
                        </Button>
                    </div>
                </div>
            </Modal>

            <AlertDialog
                open={commonAlert.open}
                onClose={handleCommonAlertClose}
            >
                {commonAlert.msg}
            </AlertDialog>
        </div>
    );
}
